import PropTypes from "prop-types";
import React from "react";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import Highlight from "@tiptap/extension-highlight";
import StarterKit from "@tiptap/starter-kit";
import Superscript from "@tiptap/extension-superscript";
import SubScript from "@tiptap/extension-subscript";
import Placeholder from "@tiptap/extension-placeholder";
import "@mantine/tiptap/styles.css";
import { Box } from "@mantine/core";
import { useStylesTipTapEditor } from "../../utils/mantine/useStylesTipTapEditor";

// Function to decode HTML entities to their actual content
const decodeHTML = (html) => {
  const textArea = document.createElement("textarea"); // Create a temporary textarea element
  textArea.innerHTML = html; // Set the HTML content as the innerHTML
  return textArea.value; // Return the decoded value
};

const TipTapEditor = ({
  value,
  onChange,
  error,
  placeholder = "",
  popup = false,
  editable = true,
}) => {
  // Decode the value for displaying in the editor
  const decodedValue = decodeHTML(value);

  // Use custom styles for the TipTap editor
  const { classes } = useStylesTipTapEditor();

  // Initialize the editor with various extensions
  // StarterKit (v3) includes Link and Underline; disable Link and use Mantine Link (Mod-k shortcut)
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link,
      Superscript,
      SubScript,
      Highlight,
      Placeholder.configure({ placeholder: placeholder }),
    ],
    editable: editable,
    content: decodedValue, // Set the decoded content as the editor's initial value
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML()); // Update the parent component with the new HTML content on change
    },
  });

  return (
    <Box className={`${classes.editor} ${popup ? classes.editorPopUp : ""}`}>
      {/* Render the RichTextEditor component with the initialized editor */}
      <RichTextEditor editor={editor}>
        {/* Toolbar with various controls for formatting */}
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          {/* Group of text style controls */}
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
            <RichTextEditor.Highlight />
            <RichTextEditor.Code />
          </RichTextEditor.ControlsGroup>

          {/* Group of heading controls */}
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>

          {/* Group of block formatting controls */}
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Blockquote />
            <RichTextEditor.Hr />
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>

          {/* Group of link controls */}
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>

          {/* Group of undo/redo controls */}
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        {/* Editor content */}
        <RichTextEditor.Content />

        {/* Display error message if there's an error */}
        {error && (
          <Box
            style={(theme) => ({
              fontSize: "0.93em",
              lineHeight: "0.93em",
              position: "absolute",
              color: theme.customColors.red,
              fontFamily: theme.headings.fontFamily.light,
              width: "23rem",
            })}
          >
            {error}
          </Box>
        )}
      </RichTextEditor>
    </Box>
  );
};

export default TipTapEditor;

TipTapEditor.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  popup: PropTypes.bool,
  editable: PropTypes.bool,
};
